
import Vue from 'vue';
import Component from 'vue-class-component';
import DollarInput from '@/core/components/DollarInput.vue';
import { namespace } from 'vuex-class';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { Prop } from 'vue-property-decorator';
import moment from 'moment';
import { AxiosError } from 'axios';
import { LoanCreateOneTimePaymentRequest } from '@/loan/types/loan-create-one-time-payment-request';
import { LoanDetails } from '@/loan/types/loan-details';
import { CoreHelper } from '@/core/helper';
import { LoanAPIService } from '@/loan/services/api';
import { FORM_DEFAULT_SELECT_OPTION } from '@/core/constants/form-default-select-option';
import { FormSelectOption } from '@/core/types/form-select-option';
import { Alert } from '@/core/types/alert';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { LoanHelper } from '@/loan/helper';
import { UtilHelper } from '@/util/helper';

const loanNamespace = namespace('loan');
const MINIMUM_PAYMENT_AMOUNT = 0.01;
const DEFAULT_ERROR_MESSAGE = 'There was an error submitting your payment, please try again.';
const DEFAULT_DESCRIPTION = 'One-Time Payment';
const SUBMITTED_PAYMENT_SUCCESS_MESSAGE = 'Payment Successsful';
const MINIMUM_PAYMENT_AMOUNT_TEXT = `Your One-Time payment must be at least ${UtilHelper.formatDollar(MINIMUM_PAYMENT_AMOUNT)}.`;
const MAXIMIMUM_PAYMENT_AMOUNT_TEXT = 'Your One-Time payment must be at or below your payoff amount and below $55,000.';

@Component({
	components: {
		DollarInput,
	},
})
export default class LoanOneTimeConfirm extends Vue {
	public alert: Alert | null = null;

	@Prop()
	public amount!: number;

	public descriptionValue: string = '';

	public fromAccountOptions: FormSelectOption[] = [];

	public fromAccountValue = FORM_DEFAULT_SELECT_OPTION.value;

	public formatDollar = UtilHelper.formatDollar;

	@loanNamespace.State('linkedAccounts')
	public linkedAccounts!: LoanLinkedAccount[];

	@loanNamespace.State('loanDetails')
	public loanDetails!: LoanDetails;

	@loanNamespace.Getter('loanId')
	public loanId!: string;

	public isPaymentLoading = false;

	public paymentValue = 0;

	public maxDwollaPaymentAmount = 55000;

	@loanNamespace.Getter('payoffAmount')
	public payoffAmount!: number;

	public wasPaymentAttempted = false;

	public wasPaymentSuccessful = false;

	get cutOffDateForDisplay(): string {
		const { cutoffDate } = this.loanDetails.oneTimePayment;
		const date = new Date(cutoffDate);
		return UtilHelper.formatDateForDisplay(date);
	}

	get cutOffTime(): string {
		const { cutoffDate } = this.loanDetails.oneTimePayment;
		return moment(cutoffDate).format('hh:mm A');
	}

	get description(): string {
		return (this.descriptionValue && this.descriptionValue.length)
			? this.descriptionValue
			: DEFAULT_DESCRIPTION;
	}

	get effectiveDateForDisplay(): string {
		return UtilHelper.formatISOStringForDisplay(this.effectiveDateISOString);
	}

	get effectiveDateISOString(): string {
		return this.loanDetails.oneTimePayment.effectiveDate;
	}

	get isPaymentButtonDisabled(): boolean {
		const isFromAccountValid = this.fromAccountValue !== FORM_DEFAULT_SELECT_OPTION.value;
		return !isFromAccountValid || (!this.isPaymentMoreThanMin || !this.isPaymentLessThanMax);
	}

	get isPaymentLessThanMax(): boolean {
		return (this.paymentValue <= this.payoffAmount) && (this.paymentValue <= this.maxDwollaPaymentAmount);
	}

	get isPaymentMoreThanMin(): boolean {
		return this.paymentValue >= MINIMUM_PAYMENT_AMOUNT;
	}

	get paymentErrorMessage(): string | null {
		if (!this.isPaymentMoreThanMin) {
			return MINIMUM_PAYMENT_AMOUNT_TEXT;
		}

		if (!this.isPaymentLessThanMax) {
			return MAXIMIMUM_PAYMENT_AMOUNT_TEXT;
		}

		return null;
	}

	get paymentHint(): string {
		return `Payoff amount: ${UtilHelper.formatDollar(this.payoffAmount)}`;
	}

	mounted(): void {
		this.fromAccountOptions = LoanHelper.getLinkedAccountsSelectOtions(this.linkedAccounts, FORM_DEFAULT_SELECT_OPTION);

		// auto select if only account AND verified
		if (this.linkedAccounts.length === 1 && this.linkedAccounts[0].verified) {
			this.fromAccountValue = this.fromAccountOptions[1]?.value;
		} else {
			this.fromAccountValue = FORM_DEFAULT_SELECT_OPTION.value;
		}

		this.paymentValue = this.amount;
	}

	public async onMakePayment(): Promise<void> {
		this.alert = null;
		this.isPaymentLoading = true;

		const request: LoanCreateOneTimePaymentRequest = {
			account: this.fromAccountValue as string,
			amount: this.paymentValue,
			description: this.description,
			effective_date: this.effectiveDateISOString,
		};

		await this.createPayment(request);
	}

	public checkIsAccountDisabled(accountOption: FormSelectOption): boolean {
		const linkedAccount = this.linkedAccounts.find((account) => account.id === accountOption.value);

		if (!linkedAccount) {
			return true;
		}

		return !linkedAccount.verified;
	}

	public linkNewAccount() {
		this.$router.push({ name: 'ProfileMyProfile', params: { modalIsOpen: 'true', paymentRefUrl: window.location.href } });
	}

	private async createPayment(request: LoanCreateOneTimePaymentRequest): Promise<void> {
		try {
			await LoanAPIService.createOneTimePayment(this.loanId, request);
			this.wasPaymentSuccessful = true;

			this.alert = {
				type: AlertTypeEnum.SUCCESS,
				message: SUBMITTED_PAYMENT_SUCCESS_MESSAGE,
			};
		} catch (error) {
			const axiosError = error as AxiosError;
			const validatedError = CoreHelper.validateAxiosError(
				axiosError,
				DEFAULT_ERROR_MESSAGE,
			);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: validatedError.message,
			};

			this.wasPaymentSuccessful = false;
		} finally {
			this.isPaymentLoading = false;
			this.wasPaymentAttempted = true;
		}
	}
}

